'use client';

import { createClient } from '@/lib/supabase/client';

export interface ForgeFood {
  id: string;
  userId: string;
  name: string;
  brand?: string;
  servingSizeG: number;
  servingUnit: string;
  calPer100g: number;
  proteinPer100g: number;
  carbsPer100g: number;
  fatPer100g: number;
  fibrePer100g: number;
  calPerServing: number;
  proteinPerServing: number;
  carbsPerServing: number;
  fatPerServing: number;
  fibrePerServing: number;
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
  source: string;
  timesUsed: number;
  createdAt: string;
}

export interface ForgeFoodLogEntry {
  id: string;
  userId: string;
  foodId?: string;
  logDate: string;
  mealType: string;
  mealLabel?: string;
  grams: number;
  cal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  fibreG: number;
  foodName: string;
  loggedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

export const forgeFoodService = {
  // ── Save a food to Hunter's database ──────────────────────
  async saveFood(food: {
    name: string;
    brand?: string;
    servingSizeG: number;
    servingUnit: string;
    calPer100g: number;
    proteinPer100g: number;
    carbsPer100g: number;
    fatPer100g: number;
    fibrePer100g: number;
    calPerServing: number;
    proteinPerServing: number;
    carbsPerServing: number;
    fatPerServing: number;
    fibrePerServing: number;
    confidence: 'high' | 'medium' | 'low';
    notes?: string;
  }): Promise<ForgeFood | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Ensure user profile exists
    await forgeFoodService.ensureProfile(user.id, user.email || '');

    try {
      const { data, error } = await supabase
        .from('forge_foods')
        .insert({
          user_id: user.id,
          name: food.name,
          brand: food.brand || null,
          serving_size_g: food.servingSizeG,
          serving_unit: food.servingUnit,
          cal_per_100g: food.calPer100g,
          protein_per_100g: food.proteinPer100g,
          carbs_per_100g: food.carbsPer100g,
          fat_per_100g: food.fatPer100g,
          fibre_per_100g: food.fibrePer100g,
          cal_per_serving: food.calPerServing,
          protein_per_serving: food.proteinPerServing,
          carbs_per_serving: food.carbsPerServing,
          fat_per_serving: food.fatPerServing,
          fibre_per_serving: food.fibrePerServing,
          confidence: food.confidence,
          notes: food.notes || null,
          source: 'ai',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Save food error:', error.message);
        return null;
      }

      return forgeFoodService.mapFood(data);
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  // ── Search Hunter's food database ─────────────────────────
  async searchFoods(query: string): Promise<ForgeFood[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('forge_foods')
        .select('*')
        .eq('user_id', user.id)
        .ilike('name', `%${query}%`)
        .order('times_used', { ascending: false })
        .limit(20);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return (data || []).map(forgeFoodService.mapFood);
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  // ── Get all foods in Hunter's database ────────────────────
  async getAllFoods(): Promise<ForgeFood[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('forge_foods').select('*').eq('user_id', user.id).order('times_used', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return (data || []).map(forgeFoodService.mapFood);
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  // ── Log a food entry ──────────────────────────────────────
  async logFood(entry: {
    foodId?: string;
    foodName: string;
    logDate?: string;
    mealType: string;
    grams: number;
    cal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    fibreG?: number;
  }): Promise<ForgeFoodLogEntry | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    await forgeFoodService.ensureProfile(user.id, user.email || '');

    const mealTypeMap: Record<string, string> = {
      'meal-breakfast': 'breakfast','meal-lunch': 'lunch','meal-dinner': 'dinner','meal-snacks': 'snacks',
    };
    const dbMealType = mealTypeMap[entry.mealType] || entry.mealType || 'snacks';

    try {
      const { data, error } = await supabase
        .from('forge_food_log_entries')
        .insert({
          user_id: user.id,
          food_id: entry.foodId || null,
          log_date: entry.logDate || new Date().toISOString().split('T')[0],
          meal_type: dbMealType,
          grams: entry.grams,
          cal: entry.cal,
          protein_g: entry.proteinG,
          carbs_g: entry.carbsG,
          fat_g: entry.fatG,
          fibre_g: entry.fibreG || 0,
          food_name: entry.foodName,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Log food error:', error.message);
        return null;
      }

      return forgeFoodService.mapLogEntry(data);
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  // ── Get today's food log ──────────────────────────────────
  async getTodayLog(): Promise<ForgeFoodLogEntry[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const today = new Date().toISOString().split('T')[0];

    try {
      const { data, error } = await supabase
        .from('forge_food_log_entries')
        .select('*')
        .eq('user_id', user.id)
        .eq('log_date', today)
        .order('logged_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return (data || []).map(forgeFoodService.mapLogEntry);
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  // ── Delete a log entry ────────────────────────────────────
  async deleteLogEntry(entryId: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('forge_food_log_entries')
        .delete()
        .eq('id', entryId)
        .eq('user_id', user.id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Delete log entry error:', error.message);
      }
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  // ── Ensure user profile exists ────────────────────────────
  async ensureProfile(userId: string, email: string): Promise<void> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('forge_user_profiles')
        .upsert({ id: userId, email, full_name: '' }, { onConflict: 'id' });
      if (error && isSchemaError(error)) throw error;
    } catch (err: any) {
      // Non-critical — profile may already exist
    }
  },

  // ── Mappers ───────────────────────────────────────────────
  mapFood(row: any): ForgeFood {
    return {
      id: row.id,
      userId: row.user_id,
      name: row.name,
      brand: row.brand,
      servingSizeG: Number(row.serving_size_g),
      servingUnit: row.serving_unit,
      calPer100g: Number(row.cal_per_100g),
      proteinPer100g: Number(row.protein_per_100g),
      carbsPer100g: Number(row.carbs_per_100g),
      fatPer100g: Number(row.fat_per_100g),
      fibrePer100g: Number(row.fibre_per_100g),
      calPerServing: Number(row.cal_per_serving),
      proteinPerServing: Number(row.protein_per_serving),
      carbsPerServing: Number(row.carbs_per_serving),
      fatPerServing: Number(row.fat_per_serving),
      fibrePerServing: Number(row.fibre_per_serving),
      confidence: row.confidence,
      notes: row.notes,
      source: row.source,
      timesUsed: row.times_used,
      createdAt: row.created_at,
    };
  },

  mapLogEntry(row: any): ForgeFoodLogEntry {
    return {
      id: row.id,
      userId: row.user_id,
      foodId: row.food_id,
      logDate: row.log_date,
      mealType: row.meal_type,
      mealLabel: row.meal_label,
      grams: Number(row.grams),
      cal: Number(row.cal),
      proteinG: Number(row.protein_g),
      carbsG: Number(row.carbs_g),
      fatG: Number(row.fat_g),
      fibreG: Number(row.fibre_g),
      foodName: row.food_name,
      loggedAt: row.logged_at,
    };
  },
};
